import { membersApi } from "@/api/members.api";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { storeToRefs } from "pinia";
import { queryClient } from "./queryClient";
import { ScopeRef } from "@/types/member";

export function useMembersDetails() {
  const appStore = storeToRefs(useAppStore());
  return useQuery({
    queryKey: ['members', { organisationId: appStore.currentOrganisationId }, 'details'],
    queryFn: async () => { 
      const membersDetails = await membersApi.getMembersDetails(toValue(appStore.currentOrganisationId));
      for (const details of membersDetails) {
        queryClient.setQueryData(['members', { organisationId: appStore.currentOrganisationId, memberId: details.member.id }, 'details'], details);
      }
      return membersDetails;
    },
    enabled: !!toValue(appStore.currentOrganisationId)
  });
}

export function useDeleteMember() {
  return useMutation({
    mutationFn: (data: { organisationId: string, memberId: string }) => membersApi.deleteMember(data.organisationId, data.memberId),
    onSuccess: (_, variables) => {
      queryClient.setQueryData(['members', { organisationId: variables.organisationId, memberId: variables.memberId }, 'details'], undefined);
      queryClient.setQueryData(['members', { organisationId: variables.organisationId, memberId: variables.memberId }, 'roles'], undefined);
      queryClient.invalidateQueries({ queryKey: ['members', { organisationId: variables.organisationId }] });
    }
  });
}

export function useUpdateMemberRole() {
  const appStore = storeToRefs(useAppStore());
  return useMutation({
    mutationFn: (data: { memberId: string, roleId: string }) => membersApi.updateMemberRole(data.memberId, data.roleId),
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({ queryKey: ['members', { organisationId: appStore.currentOrganisationId, memberId: data.memberId }, 'roles'] });
    },
  })
}

export function useMemberGrants(memberId: Ref<string>) {
  const appStore = storeToRefs(useAppStore());
  return useQuery({
    queryKey: ['members', { organisationId: appStore.currentOrganisationId, memberId: memberId }, 'grants'],
    queryFn: async () => { 
      const grants = (await membersApi.getGrants(toValue(memberId)));
      const max = Math.max(...grants.map((g) => g.role.rank));
      const highestRoleGrants = grants.filter(g => g.role.rank === max);
      for (const grantDetail of highestRoleGrants) {
        queryClient.setQueryData(['members', { organisationId: appStore.currentOrganisationId, memberId: memberId }, 'grants', { appGrantId: grantDetail.app_grant_id }], grantDetail);
      }
      return grants;
    },
    enabled: !!toValue(appStore.currentOrganisationId)
  });
}

export async function fetchMemberGrants(memberId: string) {
  const appStore = storeToRefs(useAppStore());

  return await queryClient.fetchQuery({
    queryKey: ['members', { organisationId: appStore.currentOrganisationId, memberId: memberId }, 'grants'],
    queryFn: async () => {
      const grants = await membersApi.getGrants(memberId);
      for (const grantDetail of grants) {
        queryClient.setQueryData(['members', { organisationId: appStore.currentOrganisationId, memberId: memberId }, 'grants', { appGrantId: grantDetail.app_grant_id }], grantDetail);
      }
      return grants;
    }
  });
}

export function useCreateMemberGrants() {
  const appStore = storeToRefs(useAppStore());
  return useMutation({
    mutationFn: (data: { memberId: string, roleId: string, scopes: ScopeRef[] }) => membersApi.createGrants(data.memberId, data.roleId, data.scopes),
    onSuccess: (variables, data) => {
      queryClient.invalidateQueries({ queryKey: ['members', { organisationId: appStore.currentOrganisationId, memberId: variables.memberId }, 'grants'] });
    },
  })
}

export function useDeleteMemberGrant() {
  const appStore = storeToRefs(useAppStore());
  return useMutation({
    mutationFn: (data: { memberId: string, memberGrantId: string }) => membersApi.deleteGrants(data.memberId, data.memberGrantId),
    onSuccess: (variables, data) => {
      queryClient.invalidateQueries({ queryKey: ['members', { organisationId: appStore.currentOrganisationId, memberId: variables.memberId }, 'grants'] });
    },
  })
}

export function useUpdateMemberGrants() {
  const appStore = storeToRefs(useAppStore());
  return useMutation({
    mutationFn: (data: { memberId: string, roleId: string, scopes: ScopeRef[] }) => membersApi.updateGrants(data.memberId, data.roleId, data.scopes),
    onSuccess: (variables, data) => {
      queryClient.invalidateQueries({ queryKey: ['members', { organisationId: appStore.currentOrganisationId, memberId: variables.memberId }, 'grants'] });
      queryClient.invalidateQueries({ queryKey: ['members', { organisationId: appStore.currentOrganisationId, memberId: variables.memberId }, 'roles'] });
    },
  })
}